import { useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { getExtrato } from "../services/financeiroService";
import {
  fetchExtrato,
  openBlobInTab,
  downloadBlob,
  extractBlobErrorMessage,
} from "../services/relatoriosService";
import { formatMoeda } from "../utils/moeda";
import StatCard from "../components/ui/StatCard";
import Loading, { ErrorBox } from "../components/ui/Loading";

// ─── Helpers de data/formato ───

function pad(n) {
  return String(n).padStart(2, "0");
}

function toISO(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function defaultPeriodo() {
  const hoje = new Date();
  const antes = new Date();
  antes.setDate(hoje.getDate() - 30);
  return { de: toISO(antes), ate: toISO(hoje) };
}

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// ─── Componente ───

export default function ExtratoPage() {
  const { C } = useTheme();
  const { profile } = useAuth();
  // PR3 moeda-por-guia: símbolo único da moeda do guia. Backend já manda
  // SaldoInicial/SaldoFinal/SaldoApos calculados como saldo único (additive).
  const moeda = profile?.moeda ?? "EUR";
  const fmt = (v) => formatMoeda(v, moeda);
  const initial = defaultPeriodo();
  const [de, setDe] = useState(initial.de);
  const [ate, setAte] = useState(initial.ate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);

  async function reload(novoDe, novoAte) {
    setLoading(true);
    try {
      const res = await getExtrato(novoDe, novoAte);
      setData(res);
      setError(null);
    } catch {
      setError("Não foi possível carregar o extrato.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload(de, ate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function aplicar() {
    reload(de, ate);
  }

  // Atalhos de período — seta de+ate e dispara reload com os valores novos
  // (não espera o setState pra evitar fetch com valores stale).
  function atalho(tipo) {
    const hoje = new Date();
    let novoDe;
    let novoAte = toISO(hoje);

    if (tipo === "30") {
      const d = new Date();
      d.setDate(hoje.getDate() - 30);
      novoDe = toISO(d);
    } else if (tipo === "mes") {
      novoDe = toISO(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
    } else if (tipo === "mes-passado") {
      novoDe = toISO(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));
      // Dia 0 do mês atual = último dia do mês anterior.
      novoAte = toISO(new Date(hoje.getFullYear(), hoje.getMonth(), 0));
    } else if (tipo === "90") {
      const d = new Date();
      d.setDate(hoje.getDate() - 90);
      novoDe = toISO(d);
    } else {
      return;
    }
    setDe(novoDe);
    setAte(novoAte);
    reload(novoDe, novoAte);
  }

  async function handleVisualizar() {
    if (previewBusy) return;
    // Pre-abre aba ANTES do await pra preservar user gesture (popup blocker).
    const newTab = window.open("", "_blank");
    if (!newTab) {
      alert("Permita pop-ups pra visualizar o PDF.");
      return;
    }
    setPreviewBusy(true);
    try {
      const { blob } = await fetchExtrato({ de, ate });
      openBlobInTab(newTab, blob);
    } catch (err) {
      newTab.close();
      const msg = (await extractBlobErrorMessage(err)) ?? "Erro ao gerar PDF.";
      alert(msg);
    } finally {
      setPreviewBusy(false);
    }
  }

  async function handleBaixar() {
    if (downloadBusy) return;
    setDownloadBusy(true);
    try {
      const { blob, filename } = await fetchExtrato({ de, ate });
      downloadBlob(blob, filename);
    } catch (err) {
      const msg = (await extractBlobErrorMessage(err)) ?? "Erro ao gerar PDF.";
      alert(msg);
    } finally {
      setDownloadBusy(false);
    }
  }

  // PR3 moeda-por-guia: saldo único, sem coluna BRL.
  const gridCols = "110px 2fr 1fr 1fr 1fr";
  const headers = ["Data", "Descrição", "Entrada", "Saída", "Saldo"];

  const dateInputStyle = {
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: C.inputBg,
    color: C.text,
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
  };

  return (
    <div>
      {/* ─── Header ─── */}
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
            Extrato
          </h1>
          <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
            Movimentações financeiras em ordem cronológica
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={handleVisualizar}
            disabled={previewBusy}
            title="Abrir PDF em nova aba"
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              border: "none",
              background: C.accent,
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: previewBusy ? "wait" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
              opacity: previewBusy ? 0.6 : 1,
            }}
          >
            {previewBusy ? "Carregando..." : "👁️ Visualizar Relatório"}
          </button>
          <button
            type="button"
            onClick={handleBaixar}
            disabled={downloadBusy}
            title="Baixar PDF do extrato"
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              border: `1px solid ${C.accent}`,
              background: "transparent",
              color: C.accent,
              fontSize: 12,
              fontWeight: 600,
              cursor: downloadBusy ? "wait" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
              opacity: downloadBusy ? 0.6 : 1,
            }}
          >
            {downloadBusy ? "Baixando..." : "⬇ Baixar PDF"}
          </button>
        </div>
      </div>

      {/* ─── Filtros ─── */}
      <div
        style={{
          background: C.bgCard,
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: 16,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div>
            <label
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: C.textMuted,
                display: "block",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              De
            </label>
            <input
              type="date"
              value={de}
              onChange={(e) => setDe(e.target.value)}
              style={dateInputStyle}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: C.textMuted,
                display: "block",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Até
            </label>
            <input
              type="date"
              value={ate}
              onChange={(e) => setAte(e.target.value)}
              style={dateInputStyle}
            />
          </div>
          <button
            type="button"
            onClick={aplicar}
            style={{
              padding: "10px 16px",
              borderRadius: 8,
              border: "none",
              background: C.accent,
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Aplicar
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          {[
            { tipo: "30", label: "Últimos 30 dias" },
            { tipo: "mes", label: "Este mês" },
            { tipo: "mes-passado", label: "Mês passado" },
            { tipo: "90", label: "Últimos 90 dias" },
          ].map((a) => (
            <button
              key={a.tipo}
              type="button"
              onClick={() => atalho(a.tipo)}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                border: `1px solid ${C.border}`,
                background: "transparent",
                color: C.textMuted,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* ─── Cards de saldo ─── */}
      {data && !loading && (
        <div
          style={{ display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" }}
        >
          <StatCard
            icon="📍"
            label="Saldo inicial"
            value={fmt(data.saldoInicial)}
            sub="Início do período"
            color={C.textMuted}
          />
          <StatCard
            icon="💰"
            label="Saldo atual"
            value={fmt(data.saldoFinal)}
            sub="Fim do período"
            color={data.saldoFinal >= 0 ? C.green : C.red}
          />
        </div>
      )}

      {loading && <Loading />}
      {error && <ErrorBox message={error} />}

      {/* ─── Tabela ─── */}
      {!loading && !error && data && (
        <div
          style={{
            background: C.bgCard,
            borderRadius: 14,
            border: `1px solid ${C.border}`,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              padding: "12px 20px",
              borderBottom: `1px solid ${C.border}`,
              gap: 8,
            }}
          >
            {headers.map((h, i) => (
              <span
                key={i}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.textDim,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                }}
              >
                {h}
              </span>
            ))}
          </div>

          {(data.movimentos ?? []).map((m, idx) => (
            <div
              key={`${m.financeiroId}-${idx}`}
              style={{
                display: "grid",
                gridTemplateColumns: gridCols,
                padding: "12px 20px",
                borderBottom: `1px solid ${C.border}`,
                alignItems: "center",
                gap: 8,
                // Destaque contextual: atrasado (vermelho sutil) > projetado
                // (cinza sutil) > efetivado (sem destaque).
                background: m.atrasado
                  ? C.redDim
                  : !m.pago
                  ? C.bgCardHover
                  : "transparent",
              }}
            >
              {/* Data + flag de estado */}
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: m.atrasado ? 700 : 500,
                    color: m.atrasado ? C.red : C.text,
                  }}
                >
                  {fmtDate(m.data)}
                </span>
                {m.atrasado && (
                  <span
                    style={{
                      fontSize: 9,
                      color: C.red,
                      fontStyle: "italic",
                      fontWeight: 600,
                    }}
                  >
                    Atrasado
                  </span>
                )}
                {!m.atrasado && !m.pago && (
                  <span
                    style={{
                      fontSize: 9,
                      color: C.textMuted,
                      fontStyle: "italic",
                    }}
                  >
                    🕒 Previsto
                  </span>
                )}
              </div>
              {/* Descrição */}
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {m.descricao}
                </div>
                {m.descricaoSec && (
                  <div
                    style={{
                      fontSize: 10,
                      color: C.textMuted,
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.descricaoSec}
                  </div>
                )}
              </div>
              {/* Entrada */}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: m.entrada > 0 ? C.green : C.textDim,
                }}
              >
                {m.entrada > 0 ? fmt(m.entrada) : "—"}
              </span>
              {/* Saída */}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: m.saida > 0 ? C.red : C.textDim,
                }}
              >
                {m.saida > 0 ? fmt(m.saida) : "—"}
              </span>
              {/* Saldo (moeda do guia) */}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: m.saldoApos < 0 ? C.red : C.text,
                }}
              >
                {fmt(m.saldoApos)}
              </span>
            </div>
          ))}

          {(data.movimentos ?? []).length === 0 && (
            <div
              style={{
                padding: 24,
                fontSize: 13,
                color: C.textMuted,
                textAlign: "center",
              }}
            >
              Nenhuma movimentação no período.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
